import React, { useEffect, useRef, useState, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useFBX, useTexture } from '@react-three/drei';
import * as THREE from 'three';


// ─── 3D Fish ─────────────────────────────────────────────────────────────────
function Fish({ scrollProgress }) {
  const fbx = useFBX('/assets/fish3dmodel/uploads_files_5095584_EmperorAngelfish+FBX.fbx');
  const [albedoTex, normalTex, metallicTex, aoTex] = useTexture([
    '/assets/fish3dmodel/textures/Emperator Angelfish A.png',
    '/assets/fish3dmodel/textures/Emperator Angelfish N.png',
    '/assets/fish3dmodel/textures/Emperator Angelfish M.png',
    '/assets/fish3dmodel/textures/Emperator Angelfish O.png',
  ]);

  const groupRef = useRef();
  const mixerRef = useRef();
  const lastProgress = useRef(0);
  const targetPitch = useRef(0);
  const smoothPitch = useRef(0);
  const smoothBankZ = useRef(0);
  
  // Continuously interpolated orientation parameters for realistic turnaround turns
  const targetBaseYaw = useRef(2 * Math.PI);
  const smoothBaseYaw = useRef(2 * Math.PI);

  useEffect(() => {
    if (!fbx) return;

    // Set wrapping and map orientation correctly (Blender FBX exports map UVs with flipY=true in Three.js TextureLoader)
    [albedoTex, normalTex, metallicTex, aoTex].forEach((tex) => {
      if (tex) {
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        tex.flipY = true;
        tex.needsUpdate = true;
      }
    });

    // Ensure proper color spaces
    if (albedoTex) {
      albedoTex.colorSpace = THREE.SRGBColorSpace;
      albedoTex.needsUpdate = true;
    }
    if (normalTex) {
      normalTex.colorSpace = THREE.NoColorSpace;
      normalTex.needsUpdate = true;
    }
    if (metallicTex) {
      metallicTex.colorSpace = THREE.NoColorSpace;
      metallicTex.needsUpdate = true;
    }
    if (aoTex) {
      aoTex.colorSpace = THREE.NoColorSpace;
      aoTex.needsUpdate = true;
    }

    fbx.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = false;
        child.material = new THREE.MeshPhysicalMaterial({
          map: albedoTex,
          normalMap: normalTex,
          metalness: 0.05, // Subtle organic sheen
          roughness: 0.3, // Soft lifelike texture
          clearcoat: 0.6, // Natural wet biological slime layer
          clearcoatRoughness: 0.1, // Clean, realistic highlights
          aoMap: aoTex,
          aoMapIntensity: 1.0,
          side: THREE.DoubleSide,
          transparent: false,
        });
        child.material.needsUpdate = true;
      }
    });

    if (fbx.animations?.length > 0) {
      const mixer = new THREE.AnimationMixer(fbx);
      mixerRef.current = mixer;
      const action = mixer.clipAction(fbx.animations[0]);
      action.play();
    }
  }, [fbx, albedoTex, normalTex, metallicTex, aoTex]);

  useFrame((state, delta) => {
    const t = state.clock.getElapsedTime();
    const p = scrollProgress.current;       // unbound scroll progress
    const dp = p - lastProgress.current;    // scroll delta

    // ── Animation speed ──────────────────────────────────────────
    if (mixerRef.current) {
      const speed = 0.9 + Math.abs(dp) * 40;
      mixerRef.current.update(delta * Math.min(speed, 3.5));
    }

    if (!groupRef.current) { lastProgress.current = p; return; }

    // ── Responsive Scale ──────────────────────────────────────────
    // Scale dynamically based on viewport width (clamped to range [0.018, 0.032])
    const baseScale = Math.min(state.viewport.width * 0.006, 0.032);
    const responsiveScale = Math.max(baseScale, 0.018);
    groupRef.current.scale.setScalar(responsiveScale);

    // ── World position ────────────────────────────────────────────
    // At top: fish is on the left
    // As you scroll: it swims down and snakes left and right behind panels
    // Adjust targetX amplitude dynamically based on viewport width to prevent the fish from swimming off-screen on mobile
    const amplitudeX = Math.min(state.viewport.width * 0.35, 2.2);
    const targetX = Math.sin(p * Math.PI * 0.5 - Math.PI / 2) * amplitudeX + Math.sin(t * 0.4) * 0.25;

    // Slower, more gradual diving speed driven by scroll progress
    // Y is clamped so fish never rises too high (max -0.5 world units) and never dives below the screen bottom (min -1.6)
    const rawTargetY = -0.65 - (1.0 - Math.exp(-p * 0.75)) * 0.8 + Math.sin(t * 0.5) * 0.1;
    const targetY = Math.max(Math.min(rawTargetY, -0.5), -1.6);
    const targetZ = 0.0; // Keep centered in focus depth

    // Smoothly slide to coordinates (slower vertical interpolation 0.04 for high-smoothness on mobile)
    groupRef.current.position.x += (targetX - groupRef.current.position.x) * 0.07;
    groupRef.current.position.y += (targetY - groupRef.current.position.y) * 0.04;
    groupRef.current.position.z += (targetZ - groupRef.current.position.z) * 0.07;

    // ── Turnarounds & Banking ──────────────────────────────────────
    const dx = targetX - groupRef.current.position.x;
    const dy = targetY - groupRef.current.position.y;

    // Update target yaw based on horizontal direction of motion
    if (dx > 0.005) {
      targetBaseYaw.current = 2 * Math.PI; // Face right
    } else if (dx < -0.005) {
      targetBaseYaw.current = Math.PI; // Face left
    }

    // Smooth continuous yaw transition for organic turns
    smoothBaseYaw.current += (targetBaseYaw.current - smoothBaseYaw.current) * 0.05;

    // Pitch (nose up/down) based on vertical movement
    if (Math.abs(dy) > 0.002) {
      targetPitch.current = dy > 0 ? 0.35 : -0.45;
    } else {
      targetPitch.current = 0;
    }
    smoothPitch.current += (targetPitch.current - smoothPitch.current) * 0.1;

    // Roll/Banking proportional to horizontal speed (dx)
    smoothBankZ.current += (-dx * 0.8 - smoothBankZ.current) * 0.1;

    // ── Rotation ──────────────────────────────────────────────────
    // Yaw: dynamic base heading + gentle tail swim sway
    groupRef.current.rotation.y = smoothBaseYaw.current + Math.sin(t * 1.5) * 0.08;
    // Pitch: nose tilt
    groupRef.current.rotation.x = smoothPitch.current * 0.6 + Math.sin(t * 0.5) * 0.02;
    // Roll: banking
    groupRef.current.rotation.z = smoothBankZ.current;

    lastProgress.current = p;
  });

  return (
    <group ref={groupRef} scale={[0.022, 0.022, 0.022]} position={[-1.3, -0.65, 0]}>
      <group rotation={[0, 2 * Math.PI, 0.05 * Math.PI]}>
        <primitive object={fbx} />
      </group>
    </group>
  );
}

// ─── Main ScrollFish Component ────────────────────────────────────────────────
export default function ScrollFish() {
  const scrollProgress = useRef(0);
  const [uiProgress, setUiProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const heroH = window.innerHeight;
      // Calculate uncapped scroll depth
      const p = Math.max(window.scrollY / heroH, 0);
      scrollProgress.current = p;
      
      // Keep UI progress capped to 1 so the text/prompt fade-out still triggers normally
      setUiProgress(Math.min(p, 1));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fade out arabic text as user scrolls into content
  const textOpacity = Math.max(0, 1 - uiProgress * 2.5);
  const promptOpacity = Math.max(0, 1 - uiProgress * 3.5);

  return (
    /* Full-viewport hero section — changed background to transparent to let fixed deep sea show through */
    <section style={{
      position: 'relative',
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      background: 'transparent',
    }}>

      {/* ── Fixed deep-sea background gradient ── */}
      <div style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        background: 'linear-gradient(180deg, #050d1a 0%, #0c2340 35%, #0a3d5c 65%, #0c5473 100%)',
        zIndex: -2,
        pointerEvents: 'none',
      }} />

      {/* ── 3D Canvas fixed behind UI ── */}
      <Canvas
        camera={{ position: [0, 0.5, 5.5], fov: 48 }}
        gl={{ antialias: true, alpha: true }} // Transparent WebGL canvas to show the deep sea gradient CSS background
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          zIndex: -1,
          pointerEvents: 'none'
        }}
      >
        <fog attach="fog" args={['#0c2340', 10, 24]} />

        {/* Clean, high-fidelity studio lighting setup to show vibrant, true-to-life colors */}
        <ambientLight intensity={0.5} color="#e0f2fe" />
        <directionalLight position={[5, 6, 4]} intensity={2.2} color="#ffffff" castShadow />
        <directionalLight position={[-5, -2, 2]} intensity={0.8} color="#ffffff" />
        <directionalLight position={[0, 4, -4]} intensity={1.5} color="#ffffff" />
        <pointLight position={[2, 3, 2]} intensity={1.0} color="#ffffff" />
        <pointLight position={[-3, -1, 3]} intensity={0.8} color="#ffffff" />

        <Suspense fallback={null}>
          <Fish scrollProgress={scrollProgress} />
        </Suspense>
      </Canvas>

      {/* ── Arabic Hero Text (right side or centered, fades on scroll) ── */}
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'flex-start',
        padding: isMobile ? '0 20px' : '0 8% 0 0',
        direction: 'rtl',
        zIndex: 10,
        pointerEvents: 'none',
        opacity: textOpacity,
        transition: 'opacity 0.2s ease',
      }}>
        <div style={{ 
          maxWidth: '480px', 
          textAlign: isMobile ? 'center' : 'right',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMobile ? 'center' : 'flex-start',
          width: '100%',
        }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(6, 182, 212, 0.15)',
            border: '1px solid rgba(6, 182, 212, 0.4)',
            borderRadius: '50px',
            padding: '8px 18px',
            marginBottom: '20px',
            backdropFilter: 'blur(8px)',
          }}>
            <span style={{ fontSize: '0.9rem', color: '#7dd3fc', fontWeight: '700', letterSpacing: '0.1em' }}>
              🐠 متجر الأسماك الأول في الجزائر
            </span>
          </div>

          {/* Main headline */}
          <h1 style={{
            fontSize: isMobile ? 'clamp(2rem, 8.5vw, 2.7rem)' : 'clamp(2.2rem, 5.5vw, 4.8rem)',
            fontWeight: '900',
            lineHeight: '1.15',
            margin: '0 0 18px',
            background: 'linear-gradient(135deg, #ffffff 0%, #7dd3fc 45%, #34d399 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.01em',
          }}>
            أجمل أسماك<br />الزينة في الجزائر
          </h1>

          {/* Subtitle */}
          <p style={{
            fontSize: isMobile ? '1rem' : 'clamp(1rem, 1.8vw, 1.2rem)',
            color: '#bae6fd',
            margin: '0 0 32px',
            lineHeight: '1.7',
            fontWeight: '400',
            opacity: 0.9,
            maxWidth: '100%',
          }}>
            اكتشف تشكيلتنا الرائعة من أجمل السلالات والنباتات الطبيعية والأحواض المميزة
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '10px' : '14px',
            justifyContent: isMobile ? 'center' : 'flex-start',
            flexWrap: 'nowrap',
            width: '100%',
            pointerEvents: 'all',
          }}>
            <button
              id="hero-shop-btn"
              onClick={() => document.getElementById('shop-section')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '50px',
                padding: isMobile ? '12px 22px' : '15px 34px',
                fontSize: isMobile ? '0.95rem' : '1.1rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 8px 32px rgba(16, 185, 129, 0.45)',
                transition: 'all 0.3s ease',
                fontFamily: 'inherit',
                flex: isMobile ? '1' : 'none',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(16,185,129,0.6)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(16,185,129,0.45)'; }}
            >
              تسوق الآن 🐠
            </button>

            <button
              id="hero-contact-btn"
              onClick={() => window.location.href = '/chat'}
              style={{
                background: 'rgba(255,255,255,0.07)',
                color: 'white',
                border: '1.5px solid rgba(255,255,255,0.25)',
                borderRadius: '50px',
                padding: isMobile ? '12px 20px' : '15px 30px',
                fontSize: isMobile ? '0.95rem' : '1.05rem',
                fontWeight: '600',
                cursor: 'pointer',
                backdropFilter: 'blur(12px)',
                transition: 'all 0.3s ease',
                fontFamily: 'inherit',
                flex: isMobile ? '1' : 'none',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            >
              تواصل معنا 💬
            </button>
          </div>
        </div>
      </div>

      {/* ── "Scroll down & dive in" badge bottom-left or bottom-center on mobile ── */}
      <div style={{
        position: 'absolute',
        bottom: isMobile ? '24px' : '36px',
        left: isMobile ? '50%' : '36px',
        transform: isMobile ? 'translateX(-50%)' : 'none',
        zIndex: 10,
        direction: 'rtl',
        opacity: promptOpacity,
        transition: 'opacity 0.2s ease',
        pointerEvents: 'none',
      }}>
        <div style={{
          background: 'rgba(6, 182, 212, 0.18)',
          backdropFilter: 'blur(14px)',
          border: '1px solid rgba(6, 182, 212, 0.35)',
          borderRadius: isMobile ? '30px' : '18px',
          padding: isMobile ? '10px 20px' : '18px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}>
          <div style={{ 
            color: '#f0f9ff', 
            fontWeight: '700', 
            fontSize: isMobile ? '0.9rem' : '1.05rem', 
            lineHeight: '1.4',
            whiteSpace: 'nowrap'
          }}>
            اسحب للأسفل {isMobile && 'للغوص 🌊'}
          </div>
          {!isMobile && (
            <div style={{ color: '#7dd3fc', fontWeight: '600', fontSize: '0.95rem' }}>
              ودعنا نغوص 🌊
            </div>
          )}
          <div style={{
            marginTop: '8px',
            width: '2px',
            height: isMobile ? '14px' : '36px',
            background: 'linear-gradient(180deg, #06b6d4 0%, transparent 100%)',
            margin: '8px auto 0',
            animation: 'scrollPulse 1.8s ease-in-out infinite',
          }} />
        </div>
      </div>

      {/* ── Bottom fade into dark page ── */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '150px',
        background: 'linear-gradient(to bottom, transparent 0%, #0f172a 100%)',
        pointerEvents: 'none',
        zIndex: 6,
      }} />
    </section>
  );
}
